import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

import i18n from "../../lib/i18n";
import { RootState } from "../../state";
import { Country } from "../../common/actions/Auth";
import { EntityValidationResult } from "../../lib/entities/validation";
import { media, colors } from "../../common/utils/style";
import { ScreenSize, isLarger } from "../../common/utils/media";
import Translate from "../../common/components/Translate";
import Icon from "../../common/components/Icon";
import TogglableStyledInput from "../../common/components/TogglableStyledInput";
import ErrorMessage from "../../common/components/ErrorMessage";

export type GridType = "horizontal" | "vertical";

export type CompanyValue = WtdModels.Partial<
  WtdModels.CompanyValue,
  "id" | "description" | "feature_title" | "feature_title_with_break"
>;

interface OwnProps {
  companyValue: CompanyValue;
  error?: EntityValidationResult<any>;
  gridType: GridType;
  editable: boolean;
  onChange?(companyValue: CompanyValue): void;
}

interface MappedProps {
  screenSize: ScreenSize;
  locale: string;
  country: Country;
}

type Props = MappedProps & OwnProps;

interface State {
  expanded: boolean;
}

class CompanyValueGridItem extends React.PureComponent<Props, State> {
  private descElem: HTMLElement | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      expanded: false,
    };
  }

  public maxWordLengthOfValueDesc = (country: Country) => {
    return country === "japan" ? 255 : 500;
  };

  public onChange = (text: string) => {
    const { companyValue, onChange } = this.props;
    onChange!({ ...companyValue, description: text });
  };

  public onClickShowMore = () => {
    if (!this.descElem || isLarger(this.props.screenSize, "large", true)) {
      return;
    }
    const newExpanded = !this.state.expanded;
    this.descElem.style.height = newExpanded ? "auto" : "0";
    this.setState({ expanded: newExpanded });
  };

  public render() {
    const { companyValue, gridType, editable, locale, country } = this.props;
    const { expanded } = this.state;
    const styleProps: StyleProps = { gridType, expanded };

    return (
      <Wrapper {...styleProps}>
        <TitleWrapper {...styleProps} onClick={this.onClickShowMore}>
          <Title
            {...styleProps}
            data-title={companyValue.feature_title}
            data-title-with-break={companyValue.feature_title_with_break}
          />
          <ChavronIcon name={expanded ? "wt-icon-chevron-up-o-1" : "wt-icon-chavron-down-o-1"} />
        </TitleWrapper>
        <DescriptionWrapper {...styleProps} innerRef={el => (this.descElem = el)}>
          <Description
            editable={editable}
            multiLine
            value={companyValue.description || ""}
            placeholder={i18n.translate("company.values.description", { locale })}
            maxLength={this.maxWordLengthOfValueDesc(country)}
            component="div"
            onChange={this.onChange}
          />
          {this.renderError()}
        </DescriptionWrapper>
      </Wrapper>
    );
  }

  private renderError() {
    const { error } = this.props;
    if (!error || !error.description) {
      return null;
    }
    if (error.description === "required") {
      return (
        <ErrorMsg>
          <Translate id="company.values.description_required" />
        </ErrorMsg>
      );
    }
    // TODO(KentoMoriwaki): Handle other errors
    return null;
  }
}

interface StyleProps {
  gridType: GridType;
  expanded: boolean;
}

const Wrapper = styled.div`
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  padding: 25px 0;
  ${(props: StyleProps) => (props.expanded ? "none" : "border-bottom: solid 1px #eeeeee")};
  &:first-child {
    ${(props: StyleProps) => (props.gridType === "vertical" ? "border-top: solid 1px #eeeeee" : "none")};
  }
  @media ${media.large} {
    ${(props: StyleProps) =>
      props.gridType === "vertical"
        ? css`
            width: 100%;
            flex-direction: row;
            padding: 30px 65px;
            border-top: solid 1px rgba(0, 0, 0, 0.12);
            border-bottom: none;
          `
        : css`
            flex-direction: column;
            border-top: solid 1px rgba(0, 0, 0, 0.12);
            border-bottom: solid 1px rgba(0, 0, 0, 0.12);
            padding: 40px 0 13px;
            flex-grow: 0;
            flex-basis: 33.3%;
            max-width: calc((100% - 90px) / 3);
            &:nth-child(3n + 1) {
              margin-right: 45px;
              marign-left: 0;
            }
            &:nth-child(3n) {
              margin-left: 45px;
            }
          `};
  }
`;

const TitleWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  @media ${media.large} {
    ${(props: StyleProps) =>
      props.gridType === "vertical"
        ? css`
            width: 250px;
          `
        : css`
            width: 100%;
          `};
  }
`;

const Title = styled.div`
  font-size: 18px;
  line-height: 1.52;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.8);
  &:before {
    content: attr(data-title);
  }
  @media ${media.large} {
    ${(props: StyleProps) =>
      props.gridType === "vertical"
        ? css`
            font-size: 25px;
            white-space: pre-wrap;
            &:before {
              content: attr(data-title-with-break);
            }
          `
        : css`
            width: 100%;
            font-weight: 600;
            font-size: 18px;
            text-align: center;
            color: rgba(0, 0, 0, 0.8);
          `};
  }
`;

const ChavronIcon = styled(Icon)`
  font-size: 22px;
  color: rgba(0, 0, 0, 0.12);
  margin-right: 10px;
  line-height: 0;
  @media ${media.large} {
    display: none;
  }
`;

const DescriptionWrapper = styled.div`
  flex-grow: 1;
  height: 0;
  overflow: hidden;
  @media ${media.large} {
    height: auto;
    overflow: visible;
    ${(props: StyleProps) =>
      props.gridType === "vertical"
        ? css`
            margin: 0 0 0 50px;
            max-width: calc(100% - 50px - 250px);
          `
        : css`
            margin: 20px 0 15px;
          `};
  }
`;

const Description = styled(TogglableStyledInput)`
  width: 100%;
  padding-top: 20px;
  font-weight: 400;
  font-size: 13px;
  line-height: 1.85;
  color: rgba(0, 0, 0, 0.56);
  white-space: pre-wrap;
  @media ${media.large} {
    min-height: 72px;
    padding: 0;
    font-size: 14px;
    line-height: 2;
  }
`;

const ErrorMsg = styled(ErrorMessage)`
  font-size: 13px;
  color: ${colors.red1};
`;

function mapStateToProps(state: RootState): MappedProps {
  return { screenSize: state.media.screenSize, country: state.auth.country, locale: state.auth.locale };
}

export default connect(mapStateToProps)(CompanyValueGridItem);



// WEBPACK FOOTER //
// ./frontend/src/company_values/components/CompanyValueGridItem.tsx